import { GRID_H, GRID_W, N_CELLS, NEIGHBOURS } from './types';

/** (dx + 1) * 3 + (dy + 1) -> index into NEIGHBOURS */
const DIR_LOOKUP = new Int8Array(9).fill(-1);
NEIGHBOURS.forEach((n, i) => {
  DIR_LOOKUP[(n.dx + 1) * 3 + (n.dy + 1)] = i;
});

class MinHeap {
  private keys = new Float32Array(N_CELLS * 2);
  private vals = new Int32Array(N_CELLS * 2);
  private n = 0;

  clear(): void {
    this.n = 0;
  }

  get size(): number {
    return this.n;
  }

  push(key: number, val: number): void {
    if (this.n >= this.vals.length) {
      const k = new Float32Array(this.keys.length * 2);
      const v = new Int32Array(this.vals.length * 2);
      k.set(this.keys);
      v.set(this.vals);
      this.keys = k;
      this.vals = v;
    }
    let i = this.n++;
    this.keys[i] = key;
    this.vals[i] = val;
    while (i > 0) {
      const p = (i - 1) >> 1;
      if (this.keys[p] <= this.keys[i]) break;
      this.swap(p, i);
      i = p;
    }
  }

  pop(): number {
    const top = this.vals[0];
    this.n--;
    if (this.n > 0) {
      this.keys[0] = this.keys[this.n];
      this.vals[0] = this.vals[this.n];
      let i = 0;
      for (;;) {
        const l = i * 2 + 1;
        const r = l + 1;
        let m = i;
        if (l < this.n && this.keys[l] < this.keys[m]) m = l;
        if (r < this.n && this.keys[r] < this.keys[m]) m = r;
        if (m === i) break;
        this.swap(m, i);
        i = m;
      }
    }
    return top;
  }

  private swap(a: number, b: number): void {
    const k = this.keys[a];
    this.keys[a] = this.keys[b];
    this.keys[b] = k;
    const v = this.vals[a];
    this.vals[a] = this.vals[b];
    this.vals[b] = v;
  }
}

const heap = new MinHeap();

/**
 * Dijkstra distance field over the grid, computed outward from a set of target
 * cells. `dir` at each cell points at the neighbour that leads downhill in cost,
 * so an agent only ever reads one byte to know where to step next.
 */
export class FlowField {
  readonly cost = new Float32Array(N_CELLS);
  readonly dir = new Int8Array(N_CELLS);

  compute(targets: readonly number[], cellCost: (i: number) => number): void {
    this.cost.fill(Infinity);
    this.dir.fill(-1);
    heap.clear();
    for (const t of targets) {
      if (t < 0 || t >= N_CELLS) continue;
      if (!Number.isFinite(cellCost(t))) continue;
      this.cost[t] = 0;
      heap.push(0, t);
    }
    while (heap.size > 0) {
      const i = heap.pop();
      const ci = this.cost[i];
      const x = i % GRID_W;
      const y = (i - x) / GRID_W;
      for (let n = 0; n < 8; n++) {
        const nb = NEIGHBOURS[n];
        const nx = x + nb.dx;
        const ny = y + nb.dy;
        if (nx < 0 || ny < 0 || nx >= GRID_W || ny >= GRID_H) continue;
        const j = ny * GRID_W + nx;
        const step = cellCost(j);
        if (!Number.isFinite(step)) continue;
        const nc = ci + step * (nb.w === 1 ? 1 : 1.414);
        if (nc < this.cost[j] - 1e-4) {
          this.cost[j] = nc;
          // From j, the way onward is back towards i.
          this.dir[j] = DIR_LOOKUP[(-nb.dx + 1) * 3 + (-nb.dy + 1)];
          heap.push(nc, j);
        }
      }
    }
  }

  reachable(i: number): boolean {
    return Number.isFinite(this.cost[i]);
  }

  /** Unit-ish step direction out of cell i, or null if there is nowhere to go. */
  stepFrom(i: number): { dx: number; dy: number } | null {
    const d = this.dir[i];
    if (d < 0) return null;
    return NEIGHBOURS[d];
  }
}
